import { useAuth0 } from '@auth0/auth0-react';
import { mockAuth0 } from '../services/mockAuth';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { syncUser } from '../store/userSlice';
import { AppDispatch, RootState } from '../store/store';

/**
 * Custom hook that provides authentication functionality,
 * using either real Auth0 or mock authentication based on environment settings
 */
export const useAuth = () => {
  // Get the real Auth0 context
  const auth0Context = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  
  // Get organization state from Redux
  const organizationState = useSelector((state: RootState) => state.organization);
  
  // Check if we should use mock authentication
  const isMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';
  
  // Sync the user with our backend when using mock auth
  useEffect(() => {
    if (isMockAuth) {
      // Dispatch the syncUser action with our mock user data
      dispatch(syncUser(mockAuth0.user));
    }
  }, [isMockAuth, dispatch]);
  
  // If mock auth is enabled, return mock implementation
  if (isMockAuth) {
    return {
      // Return mock auth values
      isAuthenticated: true,
      user: mockAuth0.user,
      isLoading: false,
      error: undefined,
      
      // Organization info
      organization: organizationState.current,
      organizationLoading: organizationState.loading,
      organizationError: organizationState.error,
      
      // Auth methods
      loginWithRedirect: mockAuth0.loginWithRedirect,
      logout: mockAuth0.logout,
      getAccessTokenSilently: mockAuth0.getAccessTokenSilently,
      getIdTokenClaims: mockAuth0.getIdTokenClaims,
      
      // Add any other Auth0 methods your application uses
    };
  }
  
  // Otherwise return the real Auth0 context with organization info
  return {
    ...auth0Context,
    // Organization info
    organization: organizationState.current,
    organizationLoading: organizationState.loading,
    organizationError: organizationState.error,
  };
};
